import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Coord } from '@everyboard/games';
import { CoordSet } from '@everyboard/games';
import { FlatHexaOrientation } from '@everyboard/games';
import { Player, PlayerOrNone } from '@everyboard/games';
import { PlayerNumberMap } from '@everyboard/games';
import { RulesFailure } from '@everyboard/games';
import { ScoreName } from '@everyboard/games';
import { SixFailure } from '@everyboard/games/six';
import { SixFilteredMoveGenerator } from '@everyboard/games/six';
import { SixHeuristic } from '@everyboard/games/six';
import { SixMove } from '@everyboard/games/six';
import { SixMoveGenerator } from '@everyboard/games/six';
import { SixConfig, SixLegalityInformation, SixRules } from '@everyboard/games/six';
import { SixState } from '@everyboard/games/six';
import { MGPFallible, MGPOptional, Set, MGPValidation } from '@everyboard/lib';

import { ViewBox } from '../../components/game-components/GameComponentUtils';
import { HexaLayout } from '../../components/game-components/HexaLayout';
import { ClickHandler } from '../../components/game-components/game-component/ClickHandler';
import { HexagonalGameComponent } from '../../components/game-components/game-component/HexagonalGameComponent';

type CoordAndClass = {
    coord: Coord;
    class: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-six',
    templateUrl: './six.component.html',
    styleUrls: ['../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass],
})
export class SixComponent
    extends HexagonalGameComponent<SixRules, SixMove, SixState, Player, SixConfig, SixLegalityInformation>
{

    public pieces: Coord[];
    public disconnectedCoords: CoordAndClass[] = [];
    public cuttableGroups: Coord[][] = [];
    public victoryCoords: Coord[];
    public neighbors: Coord[];
    public leftCoord: MGPOptional<Coord> = MGPOptional.empty();
    public lastDrop: MGPOptional<Coord> = MGPOptional.empty();

    public selectedPiece: MGPOptional<Coord> = MGPOptional.empty();
    public chosenLanding: MGPOptional<Coord> = MGPOptional.empty();

    private nextClickShouldSelectGroup: boolean = false;

    public constructor() {
        super('Six');
        this.aiConfig = {
            minimax: [{
                id: 'Shape',
                name: $localize`Shape`,
                heuristic: (): SixHeuristic => new SixHeuristic(),
                moveGenerator: (): SixFilteredMoveGenerator => new SixFilteredMoveGenerator(this.rules),
            }],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): SixMoveGenerator => new SixMoveGenerator(this.rules),
            }],
        };
        this.encoder = SixMove.encoder;
        this.SPACE_SIZE = 30;
        this.hexaLayout = new HexaLayout(this.SPACE_SIZE * 1.50,
                                         new Coord(this.SPACE_SIZE * 2, 0),
                                         FlatHexaOrientation.INSTANCE);
    }

    protected override getScoreName(): ScoreName {
        if (this.rules.isInDropPhase(this.state(), this.config())) {
            return ScoreName.PIECES_TO_DROP;
        } else {
            return ScoreName.REMAINING_PIECES;
        }
    }

    public override async cancelMoveAttempt(): Promise<void> {
        this.selectedPiece = MGPOptional.empty();
        this.chosenLanding = MGPOptional.empty();
        this.cuttableGroups = [];
        this.nextClickShouldSelectGroup = false;
        this.resetPiecesAndNeighbors();
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        this.resetPiecesAndNeighbors();
        this.scores = this.getScores();
    }

    private getScores(): MGPOptional<PlayerNumberMap> {
        const state: SixState = this.state();
        if (this.rules.isInDropPhase(state, this.config())) {
            return MGPOptional.of(state.countPiecesToDrop(this.config()));
        } else {
            return MGPOptional.of(state.countPiecesOnBoard());
        }
    }

    private resetPiecesAndNeighbors(): void {
        this.pieces = this.state().getPieceCoords();
        this.neighbors = this.getEmptyNeighbors();
    }

    public override hideLastMove(): void {
        this.leftCoord = MGPOptional.empty();
        this.lastDrop = MGPOptional.empty();
        this.victoryCoords = [];
        this.disconnectedCoords = [];
    }

    protected override computeViewBox(): ViewBox {
        const disconnectedCoords: Coord[] = this.disconnectedCoords.map((value: CoordAndClass) => value.coord);
        const coords: Coord[] = this.pieces.concat(disconnectedCoords).concat(this.neighbors);
        return ViewBox
            .fromHexa(coords, this.hexaLayout, this.STROKE_WIDTH)
            .expandAbove(this.SPACE_SIZE + this.STROKE_WIDTH)
            .expandBelow(this.SPACE_SIZE + this.STROKE_WIDTH)
            .expandLeft(this.SPACE_SIZE + (2 * this.STROKE_WIDTH))
            .expandRight(this.SPACE_SIZE + (2 * this.STROKE_WIDTH));
    }

    protected override async showLastMove(move: SixMove): Promise<void> {
        this.lastDrop = MGPOptional.of(move.landing);
        if (move.isDrop() === false) {
            this.leftCoord = MGPOptional.of(move.start.get());
        } else {
            this.leftCoord = MGPOptional.empty();
        }
        const state: SixState = this.state();
        if (this.rules.getGameStatus(this.node(), this.config()).isEndGame) {
            this.victoryCoords = this.rules.getShapeVictory(move, state);
        }
        this.disconnectedCoords = this.getDisconnected();
    }

    private getDisconnected(): CoordAndClass[] {
        const oldState: SixState = this.getPreviousState();
        const oldPieces: Coord[] = oldState.getPieceCoords();
        const newPieces: Coord[] = this.state().getPieceCoords();
        const disconnecteds: CoordAndClass[] =[];
        for (const oldPiece of oldPieces) {
            const start: MGPOptional<Coord> = this.node().previousMove.get().start;
            if (start.equalsValue(oldPiece) === false &&
                newPieces.some((newCoord: Coord) => newCoord.equals(oldPiece)) === false)
            {
                disconnecteds.push({
                    coord: oldPiece,
                    class: this.getPlayerClass(oldState.getPieceAt(oldPiece)),
                });
            }
        }
        const lastDrop: Coord = this.lastDrop.get();
        if (this.pieces.some((coord: Coord) => coord.equals(lastDrop)) === false &&
            newPieces.some((coord: Coord) => coord.equals(lastDrop)) === false)
        {
            disconnecteds.push({
                coord: lastDrop,
                class: this.getCurrentOpponent().getHTMLClass('-fill'),
            }); // Dummy captured their own piece
        }
        return disconnecteds;
    }

    public getEmptyNeighbors(): Coord[] {
        let legalLandings: Coord[] = this.rules.getLegalLandings(this.state());
        if (this.chosenLanding.isPresent()) {
            const chosenLanding: Coord = this.chosenLanding.get();
            legalLandings = legalLandings.filter((c: Coord) => c.equals(chosenLanding) === false);
        }
        return legalLandings;
    }

    public getPieceClass(coord: Coord): string {
        const player: PlayerOrNone = this.state().getPieceAt(coord);
        return this.getPlayerClass(player);
    }

    @ClickHandler((piece: Coord) => `#piece-${ piece.x }-${ piece.y }`)
    public async onPieceClick(piece: Coord): Promise<MGPValidation> {
        const config: SixConfig = this.config();
        const maxPiece: number = 2 * config.piecesPerPlayer;
        if (this.state().turn < maxPiece) {
            return this.cancelMove(SixFailure.CANNOT_MOVE_YET());
        } else if (this.chosenLanding.isAbsent()) {
            if (this.state().getPieceAt(piece) === this.state().getCurrentOpponent()) {
                return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            } else if (this.selectedPiece.equalsValue(piece)) {
                return this.cancelMove();
            } else {
                this.selectedPiece = MGPOptional.of(piece);
                return MGPValidation.SUCCESS;
            }
        } else {
            const cuttingMove: SixMove = SixMove.ofCut(this.selectedPiece.get(),
                                                       this.chosenLanding.get(),
                                                       piece);
            return this.chooseMove(cuttingMove);
        }
    }

    @ClickHandler((neighbor: Coord) => `#neighbor-${ neighbor.x }-${ neighbor.y }`)
    public async onNeighborClick(neighbor: Coord): Promise<MGPValidation> {
        if (this.nextClickShouldSelectGroup) {
            return this.cancelMove(SixFailure.MUST_CUT());
        }
        const config: SixConfig = this.config();
        const maxPiece: number = 2 * config.piecesPerPlayer;
        if (this.state().turn < maxPiece) {
            return this.chooseMove(SixMove.ofDrop(neighbor));
        } else {
            if (this.selectedPiece.isAbsent()) {
                return this.cancelMove(SixFailure.CAN_NO_LONGER_DROP());
            } else {
                const movement: SixMove = SixMove.ofTranslation(this.selectedPiece.get(), neighbor);
                const legality: MGPFallible<SixLegalityInformation> =
                    this.rules.isLegalPhaseTwoMove(movement, this.state());
                if (this.neededCutting(legality)) {
                    this.chosenLanding = MGPOptional.of(neighbor);
                    this.moveVirtuallyPiece();
                    this.showCuttable();
                    this.nextClickShouldSelectGroup = true;
                    return MGPValidation.SUCCESS;
                } else {
                    return this.chooseMove(movement);
                }
            }
        }
    }

    private neededCutting(legality: MGPFallible<SixLegalityInformation>): boolean {
        return legality.isFailure() && legality.getReason() === SixFailure.MUST_CUT();
    }

    private moveVirtuallyPiece(): void {
        const selectedPiece: Coord = this.selectedPiece.get();
        this.pieces = this.pieces.filter((c: Coord) => c.equals(selectedPiece) === false);
        this.neighbors = this.getEmptyNeighbors();
    }

    private showCuttable(): void {
        const movement: SixMove = SixMove.ofTranslation(this.selectedPiece.get(), this.chosenLanding.get());
        const stateAfterMove: SixState = this.state().movePiece(movement);
        const groupsAfterMove: Set<CoordSet> = stateAfterMove.getGroups();
        const biggerGroups: Set<CoordSet> = this.rules.getLargestGroups(groupsAfterMove);
        this.cuttableGroups = [];
        for (const cuttableGroup of biggerGroups) {
            this.cuttableGroups.push(cuttableGroup.toList());
        }
    }

    public getSelectedPieceClass(): string {
        if (this.chosenLanding.isPresent()) {
            return 'moved-fill';
        } else {
            return 'selected-stroke';
        }
    }

}
